/**
 * Currency utility functions for formatting and displaying currency values
 */

// Mapping of currency codes to their symbols and locales
const CURRENCY_CONFIG = {
    GBP: { symbol: '£', locale: 'en-GB' },
    USD: { symbol: '$', locale: 'en-US' },
    EUR: { symbol: '€', locale: 'de-DE' },
    CAD: { symbol: '$', locale: 'en-CA' },
    AUD: { symbol: '$', locale: 'en-AU' },
    NZD: { symbol: '$', locale: 'en-NZ' },
    JPY: { symbol: '¥', locale: 'ja-JP' },
    CHF: { symbol: 'CHF', locale: 'de-CH' },
    SEK: { symbol: 'kr', locale: 'sv-SE' },
    NOK: { symbol: 'kr', locale: 'no-NO' },
    DKK: { symbol: 'kr', locale: 'da-DK' },
};

/**
 * Get the symbol for a currency code
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'GBP', 'USD')
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '$';
    const code = String(currencyCode).toUpperCase().trim();
    return CURRENCY_CONFIG[code]?.symbol || code;
};

/**
 * Get the locale for a currency code
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'GBP', 'USD')
 * @returns {string} Locale code
 */
export const getCurrencyLocale = (currencyCode) => {
    if (!currencyCode) return 'en-GB';
    const code = String(currencyCode).toUpperCase().trim();
    return CURRENCY_CONFIG[code]?.locale || 'en-GB';
};

/**
 * Format a number as currency
 * @param {number} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code (defaults to 'GBP')
 * @param {number} decimals - Number of decimal places (defaults to 2)
 * @param {boolean} useSymbol - Whether to use symbol prefix (defaults to true)
 * @returns {string} Formatted currency string (e.g., '£1,234.56')
 */
export const formatCurrency = (value, currencyCode = 'GBP', decimals = 2, useSymbol = true) => {
    if (value == null) {
        return '—';
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
        return '—';
    }

    const locale = getCurrencyLocale(currencyCode);
    const symbol = getCurrencySymbol(currencyCode);

    const formatted = numValue.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    if (useSymbol) {
        return `${symbol}${formatted}`;
    }
    return formatted;
};

/**
 * Format a number as currency without the symbol (just the formatted number)
 * @param {number} value - The value to format
 * @param {string} currencyCode - ISO 4217 currency code (defaults to 'GBP')
 * @param {number} decimals - Number of decimal places (defaults to 2)
 * @returns {string} Formatted number string (e.g., '1,234.56')
 */
export const formatCurrencyNoSymbol = (value, currencyCode = 'GBP', decimals = 2) => {
    return formatCurrency(value, currencyCode, decimals, false);
};

const currencyUtils = {
    getCurrencySymbol,
    getCurrencyLocale,
    formatCurrency,
    formatCurrencyNoSymbol,
    CURRENCY_CONFIG,
};
export default currencyUtils;
